"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ArgColorCompact = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const use_t_1 = require("use-t");
const ContextItem_1 = require("../../ContextItem");
const ContextPane_1 = require("../../ContextPane");
const Input_1 = require("../../../../2-inline-block/Input");
const PopupControlled_1 = require("../../../Popup/PopupControlled");
const context_1 = require("../../../Popup/context");
const popup_1 = require("../../../../utils/popup");
const useLockScrolling_1 = require("../../../../hooks/useLockScrolling");
const useSingletonPopup_1 = require("../../../../hooks/useSingletonPopup");
const ColorPickerInput_1 = require("../../../ColorPicker/ColorPickerInput");
const context_2 = require("../../../../styles/context");
const RgbColor_1 = require("../../../../styles/color/RgbColor");
const OptionalBadge_1 = require("./OptionalBadge");
const DefaultableToggle_1 = require("./DefaultableToggle");
const SWATCH_SIZE = 22;
const normalizeColor = (s, allowAlpha) => {
    const rgb = RgbColor_1.RgbColor.fromString(s.trim());
    if (!rgb)
        return null;
    if (!allowAlpha && rgb.a !== 1)
        return null;
    return rgb.hex();
};
const readStructured = (v, fallback) => {
    if (v && typeof v === 'object' && 'def' in v) {
        const s = v;
        return { def: !!s.def, value: String(s.value ?? fallback) };
    }
    return { def: false, value: typeof v === 'string' && v ? v : fallback };
};
// 6×6 transparency checkerboard for swatches that may show alpha.
const checkerStyle = {
    background: 'linear-gradient(45deg, #ccc 25%, transparent 25%), ' +
        'linear-gradient(-45deg, #ccc 25%, transparent 25%), ' +
        'linear-gradient(45deg, transparent 75%, #ccc 75%), ' +
        'linear-gradient(-45deg, transparent 75%, #ccc 75%), #fff',
    backgroundSize: '6px 6px',
    backgroundPosition: '0 0, 0 3px, 3px -3px, -3px 0',
};
const ArgColorCompact = ({ param, value, onChange }) => {
    const [t] = (0, use_t_1.useT)();
    const styles = (0, context_2.useStyles)();
    const [autoHover, setAutoHover] = React.useState(false);
    const label = param.display?.() ?? t(param.name ?? param.id ?? '');
    const alphaEnabled = !!param.alpha;
    const defaultable = !!param.defaultable;
    const fallback = param.default || '#000000';
    const s = readStructured(value, fallback);
    const def = defaultable && s.def;
    const current = def ? fallback : s.value || fallback;
    const [preview, setPreview] = React.useState(current);
    const [text, setText] = React.useState(current);
    React.useEffect(() => {
        setPreview(current);
        setText(current);
    }, [current]);
    const emit = (next) => {
        if (defaultable)
            onChange?.(next);
        else
            onChange?.(next.value);
    };
    const setColor = (v) => emit({ def: false, value: v });
    const enterCustom = () => emit({ def: false, value: s.value });
    const revertToAuto = () => emit({ def: true, value: s.value });
    const onTextChange = (v) => {
        setText(v);
        const norm = normalizeColor(v, alphaEnabled);
        if (norm)
            setPreview(norm);
    };
    const commitText = () => {
        const norm = normalizeColor(text, alphaEnabled);
        if (norm && norm !== current)
            setColor(norm);
    };
    const swatchBox = (active) => (React.createElement("span", { style: {
            position: 'relative',
            display: 'inline-block',
            width: SWATCH_SIZE,
            height: SWATCH_SIZE,
            border: `2px solid ${styles.g(0.99, 0.9)}`,
            borderRadius: 4,
            boxShadow: '0px 1px 2px rgba(0,0,0,.2)',
            overflow: 'hidden',
            flexShrink: 0,
            opacity: active ? 1 : autoHover ? 1 : 0.5,
            transition: 'opacity .12s, box-shadow .12s',
            ...(alphaEnabled ? checkerStyle : {}),
        }, onMouseEnter: active ? undefined : () => setAutoHover(true), onMouseLeave: active ? undefined : () => setAutoHover(false), onClick: active ? undefined : enterCustom, onKeyDown: active
            ? undefined
            : (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    enterCustom();
                }
            } },
        React.createElement("span", { style: {
                position: 'absolute',
                inset: 0,
                background: active ? preview : current,
            } })));
    const pickerDraggingRef = React.useRef(false);
    const pendingValueRef = React.useRef(null);
    const setColorRef = React.useRef(setColor);
    setColorRef.current = setColor;
    const currentRef = React.useRef(current);
    currentRef.current = current;
    React.useEffect(() => {
        const onUp = () => {
            if (!pickerDraggingRef.current)
                return;
            pickerDraggingRef.current = false;
            const pending = pendingValueRef.current;
            pendingValueRef.current = null;
            if (pending && pending !== currentRef.current)
                setColorRef.current(pending);
        };
        document.addEventListener('pointerup', onUp);
        return () => document.removeEventListener('pointerup', onUp);
    }, []);
    const formatColor = (hsl) => hsl.toRgb().hex();
    const popup = (0, useSingletonPopup_1.useSingletonPopup)('arg-color');
    const closePopup = React.useCallback(() => popup.setOpen(false), [popup]);
    const popupContextValue = React.useMemo(() => ({ close: closePopup }), [closePopup]);
    const anchorHandle = (0, popup_1.useAnchorPointHandle)();
    (0, useLockScrolling_1.useLockScrolling)(popup.open);
    const dropRef = React.useRef(null);
    React.useEffect(() => {
        if (!popup.open)
            return;
        const handler = (e) => {
            const target = e.target;
            if (!target)
                return;
            const tog = anchorHandle.toggle;
            const drop = dropRef.current;
            if (tog && tog.contains(target))
                return;
            if (drop && drop.contains(target))
                return;
            closePopup();
        };
        document.addEventListener('mousedown', handler, true);
        return () => document.removeEventListener('mousedown', handler, true);
    }, [popup.open, anchorHandle, closePopup]);
    const renderPicker = () => (React.createElement("div", { ref: dropRef },
        React.createElement(ContextPane_1.ContextPane, { style: { padding: 12 }, onPointerDown: () => {
                pickerDraggingRef.current = true;
            } },
            React.createElement(ColorPickerInput_1.ColorPickerInput, { noAlpha: !alphaEnabled, color: preview, onChange: (hsl) => {
                    const css = formatColor(hsl);
                    setPreview(css);
                    pendingValueRef.current = css;
                } }))));
    const activeSwatch = (React.createElement(context_1.context.Provider, { value: popupContextValue },
        React.createElement(popup_1.anchorContext.Provider, { value: anchorHandle },
            React.createElement(PopupControlled_1.PopupControlled, { open: popup.open, refToggle: anchorHandle.ref, onHeadClick: () => popup.setOpen(!popup.open), onClickAway: closePopup, onEsc: popup.open ? closePopup : undefined, renderContext: renderPicker }, swatchBox(true)))));
    const customControl = (React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center', gap: 6 } },
        React.createElement("span", { style: { width: 84 } },
            React.createElement(Input_1.Input, { size: -3, mono: true, type: "text", value: text, placeholder: alphaEnabled ? '#RRGGBBAA' : '#RRGGBB', onChange: onTextChange, onBlur: commitText, onEnter: commitText })),
        activeSwatch));
    const autoDisplay = swatchBox(false);
    const right = defaultable ? (React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center', gap: 4, margin: '-8px -4px -8px 0' } }, def ? (React.createElement(React.Fragment, null,
        React.createElement(DefaultableToggle_1.DefaultableToggle, { def: true, onClick: enterCustom }),
        autoDisplay)) : (React.createElement(React.Fragment, null,
        React.createElement(DefaultableToggle_1.DefaultableToggle, { def: false, onClick: revertToAuto }),
        customControl)))) : (React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center', gap: 6, margin: '-8px -4px -8px 0' } }, customControl));
    return (React.createElement(ContextItem_1.ContextItem, { icon: param.icon?.(), control: true, inset: true, right: right },
        React.createElement("span", null,
            label,
            param.optional && React.createElement(OptionalBadge_1.OptionalBadge, null))));
};
exports.ArgColorCompact = ArgColorCompact;
